import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Paper } from '@mui/material';
import LiveMap from './Agent/components/LiveMap';
import { useAuth } from '../../AuthProvider';

function TestWebmap() {
  const { getAccessToken } = useAuth();
  const [webmapID, setWebmapID] = useState('');
  const [token, setToken] = useState(null);
  const [fetchingToken, setFetchingToken] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchToken();
  }, []);

  async function fetchToken() {
    try {
      setFetchingToken(true);
      setError(null);
      const accessToken = await getAccessToken();
      const url = process.env.REACT_APP_NODE_URL + '/admin/token';
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) throw new Error('Response error');

      const data = await response.json();
      if (data?.token) {
        setToken(data.token.access_token);
        return;
      }
      throw new Error('Token not found');
    } catch (error) {
      console.error('Error fetching token:', error);
      setError(
        'Failed to fetch ArcGIS token. Please ensure you are authenticated.',
      );
    } finally {
      setFetchingToken(false);
    }
  }

  const handleSubmit = e => {
    e.preventDefault();
    if (webmapID.trim()) {
      setError(null);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='h5' component='h1'>
          Test Webmap Viewer
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Enter a webmap ID to display it in the LiveMap component
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label='Webmap ID'
              value={webmapID}
              onChange={e => setWebmapID(e.target.value)}
              placeholder='Enter webmap ID (not _id)'
              fullWidth
              variant='outlined'
              sx={{ flex: 1 }}
            />
            <Button
              type='submit'
              variant='contained'
              sx={{
                backgroundColor: '#4FA490',
                '&:hover': { backgroundColor: '#39585C' },
                textTransform: 'none',
                minWidth: '120px',
              }}
              disabled={!webmapID.trim() || fetchingToken}
            >
              Load Map
            </Button>
          </Box>
        </form>

        {error && (
          <Typography variant='body2' color='error' sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {fetchingToken && (
          <Typography variant='body2' color='text.secondary'>
            Loading token...
          </Typography>
        )}
      </Paper>

      {webmapID.trim() && token && (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Map Preview
          </Typography>
          <Box
            sx={{
              width: '100%',
              minHeight: '500px',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <LiveMap
              webmapID={webmapID.trim()}
              token={token}
              addressInfo={null}
              defaultSettings={true}
              showDebugInfo={true}
            />
          </Box>
        </Paper>
      )}

      {webmapID.trim() && !token && !fetchingToken && (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Waiting for token to load...
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default TestWebmap;
